/**
 * Client-side geolocation helpers used by the web dashboards to
 * feed the SDK's `infoAdapter`. See
 * `docs/ai/user-signals.md` "Passive location fan-out" for how the
 * backend consumes these values.
 *
 * The old shape returned just a `"lat,lng"` string and callers
 * cached it forever. That leaked into the backend as GPS-quality
 * writes for module-scope coords that could be minutes old. The
 * new shape adds `accuracyMeters` + `capturedAt` so the backend
 * can reject stale / coarse reads.
 */

/**
 * A single geolocation snapshot from the browser. Format is chosen
 * so the SDK adapter can inline it into the three paired headers
 * without another wrapper — see
 * `libs/shared/sdks/core-sdk/src/adapters/token-adapter.ts`
 * (`currentUserLocation` + `currentUserLocationCapturedAt` +
 * `currentUserLocationAccuracyMeters`).
 */
export type BrowserLocationSnapshot = {
  /** `"lat,lng"` — exactly what the `user-location` header carries. */
  latLng: string;
  /** GPS accuracy radius reported by `GeolocationPosition.coords.accuracy`. */
  accuracyMeters: number;
  /** Epoch-ms when the coord was captured (Date.now() at read time). */
  capturedAt: number;
};

/**
 * Read the browser's current geolocation once. Returns `null`
 * when geolocation is unavailable, denied, or times out — callers
 * must treat missing location as normal.
 */
export function getBrowserLocation(): Promise<BrowserLocationSnapshot | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve(null);
  }
  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      pos =>
        resolve({
          latLng: `${pos.coords.latitude},${pos.coords.longitude}`,
          accuracyMeters: pos.coords.accuracy,
          capturedAt: Date.now(),
        }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 3000 },
    );
  });
}

/**
 * Freshness-aware cache around {@link getBrowserLocation}. Returns
 * a `read()` that:
 *
 * - Serves the cached snapshot only when it's newer than
 *   `maxAgeMs` (default 60s). Anything staler returns `null` — the
 *   SDK then omits the `user-location` header instead of shipping
 *   a coord the backend would just discard.
 * - Kicks off a background refresh whenever the cache is missing or
 *   stale, so the next call has fresh data ready. The refresh is
 *   fire-and-forget — never awaited by `read()`.
 *
 * ### Why not just refresh on every request?
 *
 * `navigator.geolocation.getCurrentPosition` is not free — on
 * desktop without a GPS radio it can take hundreds of milliseconds
 * and prompt the user. Doing that on every SDK call would tank
 * latency and pop the OS prompt during normal usage. The cache
 * lets one refresh serve many requests within its freshness window.
 *
 * ### Why serve `null` on staleness instead of the stale value?
 *
 * The backend cannot tell "moved 5 km ago" from "phone was in a
 * tunnel for 5 min". Sending a coord we know is stale would poison
 * the `Driver.meta.lastKnownLocation` used by the fleet god-view
 * and agent tools. Better to omit the header and let the last
 * good snapshot age out on the backend side.
 */
export function createBrowserLocationCache(
  options: { maxAgeMs?: number } = {},
): {
  read: () => BrowserLocationSnapshot | null;
  refresh: () => void;
} {
  const maxAgeMs = options.maxAgeMs ?? 60_000;
  let cached: BrowserLocationSnapshot | null = null;
  let refreshing = false;

  const refresh = (): void => {
    if (refreshing) return;
    refreshing = true;
    getBrowserLocation()
      .then(snap => {
        if (snap) cached = snap;
      })
      .catch(() => {
        /* swallow — location is best-effort */
      })
      .finally(() => {
        refreshing = false;
      });
  };

  // Kick off an initial background read so the first authed request
  // after boot has a chance of finding fresh coords in the cache.
  refresh();

  const read = (): BrowserLocationSnapshot | null => {
    if (!cached) {
      refresh();
      return null;
    }
    const age = Date.now() - cached.capturedAt;
    if (age >= maxAgeMs) {
      // Stale — refresh in the background but don't serve the old
      // value to the SDK.
      refresh();
      return null;
    }
    return cached;
  };

  return { read, refresh };
}
